import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CalendarView from './components/CalendarView';
import OccasionCard from './components/OccasionCard';
import AddOccasionModal from './components/AddOccasionModal';
import CulturalCelebrations from './components/CulturalCelebrations';
import UpcomingOccasions from './components/UpcomingOccasions';
import { occasionsService } from '../../services/occasions';

const Occasions = () => {
  const [activeTab, setActiveTab] = useState('calendar');
  const [selectedDate, setSelectedDate] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [occasions, setOccasions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Mock occasions data
  const mockOccasions = [
    {
      id: 1,
      title: "Mom\'s Birthday",
      category: "birthday",
      date: "2024-12-25",
      recipients: ["Sarah Johnson"],
      budget: "150",
      notes: "She loves gardening and reading mystery novels. Consider a plant or book set.",
      giftStatus: "in_progress",
      reminders: {
        twoWeeks: true,
        oneWeek: true,
        threeDays: true,
        oneDay: true
      }
    },
    {
      id: 2,
      title: "Wedding Anniversary",
      category: "anniversary",
      date: "2024-12-31",
      recipients: ["John & Lisa"],
      budget: "200",
      notes: "25th anniversary - silver theme would be perfect",
      giftStatus: "not_started",
      reminders: {
        twoWeeks: true,
        oneWeek: true,
        threeDays: false,
        oneDay: true
      }
    },
    {
      id: 3,
      title: "Christmas",
      category: "holiday",
      date: "2024-12-25",
      recipients: ["Family"],
      budget: "500",
      notes: "Need gifts for 6 family members",
      giftStatus: "completed",
      reminders: {
        twoWeeks: true,
        oneWeek: true,
        threeDays: true,
        oneDay: true
      }
    },
    {
      id: 4,
      title: "Emma\'s Graduation",
      category: "graduation",
      date: "2025-01-15",
      recipients: ["Emma Wilson"],
      budget: "100",
      notes: "Graduating from college with a degree in Computer Science",
      giftStatus: "not_started",
      reminders: {
        twoWeeks: true,
        oneWeek: false,
        threeDays: false,
        oneDay: true
      }
    },
    {
      id: 5,
      title: "Baby Shower",
      category: "baby",
      date: "2025-02-10",
      recipients: ["Jessica & Mike"],
      budget: "75",
      notes: "First baby, expecting a girl",
      giftStatus: "in_progress",
      reminders: {
        twoWeeks: true,
        oneWeek: true,
        threeDays: true,
        oneDay: true
      }
    },
    {
      id: 6,
      title: "Valentine\'s Day",
      category: "holiday",
      date: "2025-02-14",
      recipients: ["Partner"],
      budget: "120",
      notes: "Something romantic and thoughtful",
      giftStatus: "not_started",
      reminders: {
        twoWeeks: true,
        oneWeek: true,
        threeDays: true,
        oneDay: true
      }
    }
  ];

  // Load occasions from API
  const loadOccasions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await occasionsService.getOccasions();
      setOccasions(data.results || data);
    } catch (err) {
      setError('Failed to load occasions. Please try again.');
      console.error('Error loading occasions:', err);
      // Fallback to mock data if API fails
      setOccasions(mockOccasions);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOccasions();
  }, []);

  const tabs = [
    { id: 'calendar', label: 'Calendar View', icon: 'Calendar' },
    { id: 'upcoming', label: 'Upcoming', icon: 'Clock' },
    { id: 'all', label: 'All Occasions', icon: 'List' },
    { id: 'cultural', label: 'Cultural Celebrations', icon: 'Globe' }
  ];

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'birthday', label: 'Birthdays' },
    { value: 'anniversary', label: 'Anniversaries' },
    { value: 'holiday', label: 'Holidays' },
    { value: 'graduation', label: 'Graduations' },
    { value: 'wedding', label: 'Weddings' },
    { value: 'baby', label: 'Baby Events' }
  ];

  const handleAddOccasion = async (occasionData) => {
    try {
      const newOccasion = await occasionsService.createOccasion(occasionData);
      setOccasions(prev => [...prev, newOccasion]);
      setIsAddModalOpen(false);
    } catch (err) {
      console.error('Error creating occasion:', err);
      // You might want to show an error message to the user here
    }
  };

  const handleEditOccasion = (occasion) => {
    console.log('Edit occasion:', occasion);
    // Implementation for editing occasion
  };

  const handleDeleteOccasion = async (occasionId) => {
    try {
      await occasionsService.deleteOccasion(occasionId);
      setOccasions(prev => prev.filter(occasion => occasion.id !== occasionId));
    } catch (err) {
      console.error('Error deleting occasion:', err);
      // You might want to show an error message to the user here
    }
  };

  const handleViewGifts = (occasion) => {
    console.log('View gifts for:', occasion);
    // Navigate to gift finder with occasion context
  };

  const handleOccasionClick = (occasion) => {
    console.log('Occasion clicked:', occasion);
    // Show occasion details or navigate to gift planning
  };

  const filteredOccasions = occasions.filter(occasion => {
    const matchesSearch = occasion.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      occasion.recipients.some(recipient =>
        recipient.toLowerCase().includes(searchQuery.toLowerCase())
      );
    const matchesCategory = filterCategory === 'all' || occasion.category === filterCategory;

    return matchesSearch && matchesCategory;
  });

  const renderTabContent = () => {
    switch (activeTab) {
      case 'calendar':
        return (
          <CalendarView
            occasions={occasions}
            selectedDate={selectedDate}
            onDateSelect={setSelectedDate}
            onOccasionClick={handleOccasionClick}
          />
        );

      case 'upcoming':
        return (
          <UpcomingOccasions
            occasions={occasions}
            onOccasionClick={handleOccasionClick}
          />
        );

      case 'cultural':
        return <CulturalCelebrations />;

      default:
        return (
          <div className="space-y-6">
            {/* Search and Filter */}
            <div className="bg-white rounded-2xl shadow-soft border border-accent-100 p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                <div className="flex-1 max-w-md">
                  <div className="relative">
                    <Icon
                      name="Search"
                      size={20}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent-400"
                    />
                    <input
                      type="text"
                      placeholder="Search occasions or recipients..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-accent-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="px-4 py-3 border border-accent-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                  >
                    {categories.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>

                  <Button
                    variant="primary"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={() => setIsAddModalOpen(true)}
                    className="shadow-warm"
                  >
                    Add Occasion
                  </Button>
                </div>
              </div>
            </div>

            {/* Occasions Grid */}
            {filteredOccasions.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredOccasions.map(occasion => (
                  <OccasionCard
                    key={occasion.id}
                    occasion={occasion}
                    onEdit={handleEditOccasion}
                    onDelete={handleDeleteOccasion}
                    onViewGifts={handleViewGifts}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-soft border border-accent-100 p-12 text-center">
                <div className="w-20 h-20 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="Calendar" size={40} className="text-accent-500" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-accent-800 mb-3">
                  No Occasions Found
                </h3>
                <p className="text-accent-600 mb-6 max-w-md mx-auto">
                  {searchQuery || filterCategory !== 'all'
                    ? "Try adjusting your search or filter criteria." : "Start by adding your first occasion to never miss an important celebration."
                  }
                </p>
                <Button
                  variant="primary"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => setIsAddModalOpen(true)}
                  className="shadow-warm"
                >
                  Add Your First Occasion
                </Button>
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-surface via-white to-primary-50">
      <Helmet>
        <title>Occasions - Giftsy Modern</title>
        <meta name="description" content="Manage all your gift-giving occasions with our intelligent calendar system. Never miss a birthday, anniversary, or special celebration again." />
      </Helmet>

      <Header />

      <main className="pt-20 lg:pt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-2xl mb-6 shadow-warm">
              <Icon name="Calendar" size={32} color="white" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-heading font-bold text-accent-800 mb-4">
              Occasions Central
            </h1>
            <p className="text-xl text-accent-600 max-w-3xl mx-auto leading-relaxed">
              Your comprehensive gift-giving command center. Track important dates, plan ahead, and never miss a meaningful celebration.
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-2xl shadow-soft border border-accent-100 p-6 text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Icon name="Calendar" size={24} className="text-primary-600" />
              </div>
              <div className="text-2xl font-bold text-accent-800 mb-1">
                {occasions.length}
              </div>
              <div className="text-sm text-accent-600">Total Occasions</div>
            </div>

            <div className="bg-white rounded-2xl shadow-soft border border-accent-100 p-6 text-center">
              <div className="w-12 h-12 bg-warning-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Icon name="Clock" size={24} className="text-warning-600" />
              </div>
              <div className="text-2xl font-bold text-accent-800 mb-1">
                {occasions.filter(o => {
                  const today = new Date();
                  const occasionDate = new Date(o.date);
                  const diffDays = Math.ceil((occasionDate - today) / (1000 * 60 * 60 * 24));
                  return diffDays >= 0 && diffDays <= 30;
                }).length}
              </div>
              <div className="text-sm text-accent-600">This Month</div>
            </div>

            <div className="bg-white rounded-2xl shadow-soft border border-accent-100 p-6 text-center">
              <div className="w-12 h-12 bg-success-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Icon name="CheckCircle" size={24} className="text-success-600" />
              </div>
              <div className="text-2xl font-bold text-accent-800 mb-1">
                {occasions.filter(o => o.giftStatus === 'completed').length}
              </div>
              <div className="text-sm text-accent-600">Gifts Ready</div>
            </div>

            <div className="bg-white rounded-2xl shadow-soft border border-accent-100 p-6 text-center">
              <div className="w-12 h-12 bg-secondary-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Icon name="Users" size={24} className="text-secondary-600" />
              </div>
              <div className="text-2xl font-bold text-accent-800 mb-1">
                {new Set(occasions.flatMap(o => o.recipients)).size}
              </div>
              <div className="text-sm text-accent-600">Recipients</div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-white rounded-2xl shadow-soft border border-accent-100 mb-8 overflow-hidden">
            <div className="flex overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-4 font-medium text-sm transition-all duration-200 whitespace-nowrap border-b-2 ${activeTab === tab.id
                    ? 'text-primary-600 border-primary-500 bg-primary-50' : 'text-accent-600 border-transparent hover:text-accent-800 hover:bg-surface'
                    }`}
                >
                  <Icon name={tab.icon} size={18} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="mb-8">
            {renderTabContent()}
          </div>

          {/* Quick Actions */}
          <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-2xl p-8 border border-accent-100">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-heading font-semibold text-accent-800 mb-2">
                Quick Actions
              </h2>
              <p className="text-accent-600">
                Streamline your gift-giving process with these helpful shortcuts
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button
                variant="outline"
                size="lg"
                iconName="Plus"
                iconPosition="left"
                onClick={() => setIsAddModalOpen(true)}
                className="justify-center"
              >
                Add Occasion
              </Button>

              <Button
                variant="outline"
                size="lg"
                iconName="Search"
                iconPosition="left"
                className="justify-center"
              >
                Find Gifts
              </Button>

              <Button
                variant="outline"
                size="lg"
                iconName="Bell"
                iconPosition="left"
                className="justify-center"
              >
                Set Reminders
              </Button>
            </div>
          </div>
        </div>
      </main>

      {/* Add Occasion Modal */}
      <AddOccasionModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddOccasion}
      />
    </div>
  );
};

export default Occasions;